/**
 * Notification Types & Data Model
 * Version: 6.2.0
 * 
 * Central notification definitions for the ZO Bull platform
 */

// ============================================
// Notification Categories
// ============================================
export enum NotificationCategory {
  TRADE = 'trade',
  ALERT = 'alert',
  SYSTEM = 'system',
  BOT = 'bot',
  PORTFOLIO = 'portfolio',
  MARKET = 'market',
}

// ============================================
// Notification Priority
// ============================================
export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

// ============================================
// Notification Status
// ============================================
export enum NotificationStatus {
  UNREAD = 'unread',
  READ = 'read',
  ARCHIVED = 'archived',
}

// ============================================
// Notification Actions
// ============================================
export interface NotificationAction {
  id: string
  label: string
  action: string
  variant?: 'primary' | 'secondary' | 'danger'
}

// ============================================
// Notification Icon Mapping
// ============================================
export const NotificationIcons: Record<NotificationCategory, string> = {
  [NotificationCategory.TRADE]: '📈',
  [NotificationCategory.ALERT]: '⚠️',
  [NotificationCategory.SYSTEM]: '🔧',
  [NotificationCategory.BOT]: '🤖',
  [NotificationCategory.PORTFOLIO]: '💼',
  [NotificationCategory.MARKET]: '📊',
}

// ============================================
// Priority Colors
// ============================================
export const PriorityColors: Record<NotificationPriority, string> = {
  [NotificationPriority.LOW]: '#888',
  [NotificationPriority.NORMAL]: '#6366f1',
  [NotificationPriority.HIGH]: '#f97316',
  [NotificationPriority.URGENT]: '#ef4444',
}

// ============================================
// Base Notification Interface
// ============================================
export interface NotificationBase {
  id: string
  title: string
  message: string
  category: NotificationCategory
  priority: NotificationPriority
  status: NotificationStatus
  timestamp: Date
  readAt?: Date
  actions?: NotificationAction[]
  icon?: string
  color?: string
}

// ============================================
// Trade Notification
// ============================================
export interface TradeNotification extends NotificationBase {
  category: NotificationCategory.TRADE
  data: {
    orderId: string
    symbol: string
    type: 'BUY' | 'SELL'
    quantity: number
    price: number
    pnl?: number
    botName: string
    strategyName?: string
  }
}

// ============================================
// Alert Notification
// ============================================
export interface AlertNotification extends NotificationBase {
  category: NotificationCategory.ALERT
  data: {
    alertType: 'price' | 'volume' | 'indicator' | 'custom'
    symbol: string
    condition: string
    currentValue: number
    threshold: number
    triggeredAt: Date
  }
}

// ============================================
// System Notification
// ============================================
export interface SystemNotification extends NotificationBase {
  category: NotificationCategory.SYSTEM
  data: {
    systemType: 'maintenance' | 'update' | 'error' | 'info'
    version?: string
    downtime?: Date
  }
}

// ============================================
// Bot Notification
// ============================================
export interface BotNotification extends NotificationBase {
  category: NotificationCategory.BOT
  data: {
    botId: string
    botName: string
    eventType: 'started' | 'stopped' | 'paused' | 'error' | 'completed'
    reason?: string
  }
}

// ============================================
// Portfolio Notification
// ============================================
export interface PortfolioNotification extends NotificationBase {
  category: NotificationCategory.PORTFOLIO
  data: {
    metricType: 'pnl' | 'balance' | 'margin' | 'drawdown'
    currentValue: number
    changeValue: number
    changePercent: number
    threshold?: number
  }
}

// ============================================
// Market Notification
// ============================================
export interface MarketNotification extends NotificationBase {
  category: NotificationCategory.MARKET
  data: {
    marketType: 'market_open' | 'market_close' | 'holiday' | 'news'
    symbol?: string
    description: string
    affectedSegments?: string[]
  }
}

// ============================================
// Union Type for All Notifications
// ============================================
export type Notification = 
  | TradeNotification 
  | AlertNotification 
  | SystemNotification 
  | BotNotification 
  | PortfolioNotification 
  | MarketNotification

// ============================================
// Notification Filter
// ============================================
export interface NotificationFilter {
  categories?: NotificationCategory[]
  priorities?: NotificationPriority[]
  status?: NotificationStatus
  searchQuery?: string
  dateRange?: {
    start: Date
    end: Date
  }
}

// ============================================
// Notification Summary
// ============================================
export interface NotificationSummary {
  total: number
  unread: number
  byCategory: Record<NotificationCategory, number>
  byPriority: Record<NotificationPriority, number>
  recent: Notification[]
}

// ============================================
// Notification Settings
// ============================================
export interface NotificationSettings {
  enabled: boolean
  sound: boolean
  pushEnabled: boolean
  emailEnabled: boolean
  
  // Category preferences
  tradeEnabled: boolean
  alertEnabled: boolean
  systemEnabled: boolean
  botEnabled: boolean
  portfolioEnabled: boolean
  marketEnabled: boolean
  
  // Priority thresholds
  minPriorityAlert: NotificationPriority
  
  // Quiet hours
  quietHoursEnabled: boolean
  quietHoursStart?: string
  quietHoursEnd?: string
}

// ============================================
// Default Settings Factory
// ============================================
export function createDefaultSettings(): NotificationSettings {
  return {
    enabled: true,
    sound: true,
    pushEnabled: true,
    emailEnabled: false,
    
    tradeEnabled: true,
    alertEnabled: true,
    systemEnabled: true,
    botEnabled: true,
    portfolioEnabled: true,
    marketEnabled: true,
    
    minPriorityAlert: NotificationPriority.NORMAL,
    
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
  }
}

// ============================================
// Notification Builder Helper
// ============================================
export function createNotification(
  partial: {
    title: string
    message: string
    category: NotificationCategory
    priority?: NotificationPriority
    status?: NotificationStatus
    timestamp?: Date
    icon?: string
    color?: string
    data?: any
  }
): Notification {
  const priority = partial.priority || NotificationPriority.NORMAL
  return {
    id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    title: partial.title,
    message: partial.message,
    category: partial.category,
    priority,
    status: partial.status || NotificationStatus.UNREAD,
    timestamp: partial.timestamp || new Date(),
    icon: partial.icon || NotificationIcons[partial.category],
    color: partial.color || PriorityColors[priority],
    data: partial.data,
  } as Notification
}

// ============================================
// Time Formatting Helper
// ============================================
export function formatNotificationTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}